// Tiện ích dùng chung khi thao tác `rooms/{roomId}/tenants`.

import {RoomTenant} from '../types/roomTenant';

export const sameEmail = (a?: string, b?: string): boolean =>
    !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

// Đọc: chấp nhận cả dạng cũ (mảng) lẫn dạng mới (map theo id)
export const toTenantArray = (value: unknown): RoomTenant[] => {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (value && typeof value === 'object') return Object.values(value as Record<string, RoomTenant>);
    return [];
};

/**
 * Ghi: LUÔN ghi dạng map `{tenantId: {...}}`, không ghi mảng nữa.
 *
 * Lý do: rules RTDB không duyệt được mảng, và xoá một phần tử giữa mảng làm dồn
 * index nên không thể phân biệt "sửa hồ sơ của mình" với "xoá hồ sơ người khác".
 * Với map thì mỗi hồ sơ là một node cố định, rules kiểm được từng cái một
 * (xem nhánh rooms/$roomId/tenants/$tenantId trong database.rules.json).
 */
export const toTenantMap = (tenants: RoomTenant[]): Record<string, RoomTenant> | null => {
    if (tenants.length === 0) return null;   // null = xoá node
    return tenants.reduce<Record<string, RoomTenant>>((acc, tenant, index) => {
        const key = tenant.id || `${Date.now()}${index}`;
        acc[key] = {...tenant, id: key};
        return acc;
    }, {});
};

/**
 * Người thuê đứng tên phòng trên hoá đơn / danh sách.
 *
 * KHÔNG còn khái niệm "người đại diện" do chủ nhà bấm chọn: từ khi mọi người
 * đăng nhập bằng Google, cờ đó không quyết định quyền gì nữa, chỉ là một ô
 * chủ nhà phải nhớ tick. Giờ lấy đơn giản - ưu tiên hồ sơ đã gắn tài khoản,
 * không thì hồ sơ đầu danh sách.
 */
export const getPrimaryTenant = (tenants: unknown): RoomTenant | undefined => {
    const list = toTenantArray(tenants);
    return list.find(tenant => !!tenant.email) || list[0];
};

/** Tên gộp của cả phòng, vd "Trinh, Hoàng" - dùng cho hoá đơn và thẻ phòng */
export const getTenantNames = (tenants: unknown): string =>
    toTenantArray(tenants).map(tenant => tenant.name).filter(Boolean).join(', ');

/**
 * Dòng phụ dưới tên phòng ở mọi danh sách/thẻ: ai đang ở phòng này.
 *
 * Trước kia chỗ này hiện `description` vì hồ sơ người thuê chưa có, chủ nhà gõ
 * tạm tên vào mô tả ngắn - hai chỗ ghi cùng một thứ và luôn lệch nhau. Giờ tên
 * lấy thẳng từ hồ sơ, mô tả ngắn chỉ còn là ghi chú tự do và làm dự phòng cho
 * phòng chưa có hồ sơ nào.
 */
export const getRoomSubtitle = (room: { tenants?: unknown; description?: string }): string =>
    getTenantNames(room.tenants) || room.description || '';
